import React, { useState } from 'react';
import { Dialog, Box, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CommonImages from '../../../commons/images';
import * as styles from './styles';

const SHARE_URL = 'https://ecal.world/';

function buildShareContent(publisher) {
    const name = publisher?.name;
    const message = name
        ? `Check out ${name} on @ecalworld — sync their events straight to your calendar! ${SHARE_URL}`
        : `I've just synced my fave events to my calendar via @ecalworld, you can too! ${SHARE_URL}`;
    return { message, url: SHARE_URL };
}

function buildShareButtons(message) {
    return [
        {
            label: 'WhatsApp',
            href: `https://wa.me/?text=${encodeURIComponent(message)}`,
        },
        {
            label: 'Twitter / X',
            href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`,
        },
        {
            label: 'Facebook',
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}&quote=${encodeURIComponent(message)}`,
        },
        {
            label: 'Email',
            href: `mailto:?subject=${encodeURIComponent('Sync your favourite events to your calendar with ECAL')}&body=${encodeURIComponent(message)}`,
        },
    ];
}

function ShareDialog({ open, publisher, onClose }) {
    const [copied, setCopied] = useState(false);
    const { message } = buildShareContent(publisher);
    const shareButtons = buildShareButtons(message);
    const title = publisher?.name ? `Share ${publisher.name}` : 'Share ECAL World';

    function handleCopyLink() {
        navigator.clipboard.writeText(SHARE_URL).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        }).catch(() => {});
    }

    function renderCloseButton() {
        return (
            <Box component="button" onClick={onClose} aria-label="Close" sx={styles.closeButton}>
                <CloseIcon sx={{ fontSize: 16 }} />
            </Box>
        );
    }

    function renderHeader() {
        return (
            <Box sx={styles.header}>
                <Box component="img" src={CommonImages.ECAL_SYMBOL} alt="ECAL" sx={styles.logo} />
                <Typography sx={styles.title}>{title}</Typography>
            </Box>
        );
    }

    function renderDescription() {
        return (
            <Typography sx={styles.description}>
                Tell a friend about ECAL — discover and sync the world's best brands to your personal digital calendar.
            </Typography>
        );
    }

    function renderMessageBox() {
        return (
            <Box sx={styles.messageBox}>
                "{message}"
            </Box>
        );
    }

    function renderShareButton({ label, href }) {
        return (
            <Box key={label} sx={styles.shareButtonWrapper}>
                <Box
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noreferrer noopener"
                    sx={styles.shareButton}
                >
                    {label}
                </Box>
            </Box>
        );
    }

    function renderShareButtons() {
        return (
            <Box sx={styles.buttonsGrid}>
                {shareButtons.map(renderShareButton)}
            </Box>
        );
    }

    function renderCopyButton() {
        return (
            <Box component="button" onClick={handleCopyLink} sx={styles.copyButton}>
                {copied ? 'Link copied!' : 'Copy link'}
            </Box>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            aria-label="Share ECAL World"
            sx={styles.dialog}
            slotProps={{ backdrop: { sx: styles.backdrop } }}
        >
            {renderCloseButton()}
            {renderHeader()}
            {renderDescription()}
            {renderMessageBox()}
            {renderShareButtons()}
            {renderCopyButton()}
        </Dialog>
    );
}

export default ShareDialog;
